import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGameEngine } from '../hooks/useGameEngine';
import { useResponsive } from '../hooks/useResponsive';
import { useVibration } from '../hooks/useVibration';
import { GAME_MODES } from '../utils/gameData';
import EnhancedBubble from '../components/game/EnhancedBubble';
import HUD from '../components/game/HUD';
import MobileHUD from '../components/game/MobileHUD';
import MobileControls from '../components/game/MobileControls';
import PauseMenu from '../components/game/PauseMenu';
import HelpPanel from '../components/game/HelpPanel';
import AchievementsPanel from '../components/game/AchievementsPanel';
import AchievementNotification from '../components/game/AchievementNotification';
import Powerups from '../components/game/Powerups';
import Countdown from '../components/game/Countdown';
import PointsAnimation from '../components/game/PointsAnimation';
import ComboDisplay from '../components/game/ComboDisplay';
import LevelUpNotification from '../components/game/LevelUpNotification';
import PowerupNotification from '../components/game/PowerupNotification';
import SpeedIndicator from '../components/game/SpeedIndicator';
import TouchFeedback from '../components/game/TouchFeedback';
import PerformanceMonitor from '../components/game/PerformanceMonitor';
import GameBackground from '../components/game/GameBackground';
import SEOHead from '../components/SEO/SEOHead';
import { audioManager } from '../utils/audioManager';

const GamePlay = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || GAME_MODES.TIME_CHALLENGE;

  const { isMobile } = useResponsive();
  const { vibrateTap, vibrateSuccess } = useVibration();

  const {
    gameState,
    score,
    timeLeft,
    level,
    bubbles,
    combo,
    maxCombo,
    stats,
    powerups,
    unlockedAchievements,
    notifications,
    startGame,
    pauseGame,
    resumeGame,
    handlePop,
    handleRemove,
    removeNotification
  } = useGameEngine(mode);

  const [showPause, setShowPause] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showCountdown, setShowCountdown] = useState(true);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [pointsAnimations, setPointsAnimations] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [showPerformance] = useState(false);

  const displayedAchievementIds = useRef(new Set());
  const hasNavigated = useRef(false);
  const isGameEnding = useRef(false);
  const isShowingAchievement = useRef(false);

  const getModeTitle = () => {
    switch(mode) {
      case GAME_MODES.TIME_CHALLENGE: return '時間挑戰';
      case GAME_MODES.ENDLESS: return '無盡模式';
      case GAME_MODES.PRECISION: return '精準模式';
      default: return '遊戲中';
    }
  };

  useEffect(() => {
    if (gameState === 'ready') {
      audioManager.init();
    }
  }, [gameState]);

  useEffect(() => {
    if (gameState === 'ended' && !hasNavigated.current && !isGameEnding.current) {
      isGameEnding.current = true;
      
      setCurrentAchievement(null);
      isShowingAchievement.current = false;
      
      setTimeout(() => {
        hasNavigated.current = true;
        
        const resultState = {
          score,
          level,
          mode,
          stats: {
            ...stats,
            maxCombo: maxCombo || stats.maxCombo || 0
          },
          unlockedAchievements
        };

        console.log('Navigating to results with state:', resultState);
        
        navigate('/results', {
          state: resultState,
          replace: true
        });
      }, 500);
    }
  }, [gameState, score, level, mode, stats, maxCombo, unlockedAchievements, navigate]);

  useEffect(() => {
    if (gameState === 'ended' || isGameEnding.current) {
      return;
    }

    if (isShowingAchievement.current) {
      return;
    }

    if (currentAchievement) {
      return;
    }

    if (unlockedAchievements.length > 0) {
      const newAchievement = unlockedAchievements.find(
        achievement => !displayedAchievementIds.current.has(achievement.id)
      );

      if (newAchievement) {
        console.log('Showing new achievement:', newAchievement.name);
        displayedAchievementIds.current.add(newAchievement.id);
        isShowingAchievement.current = true;
        setCurrentAchievement(newAchievement);
        vibrateSuccess();
      }
    }
  }, [unlockedAchievements, currentAchievement, vibrateSuccess, gameState]);

  const handleCountdownComplete = () => {
    setShowCountdown(false);
    startGame();
  };

  const handlePauseClick = () => {
    pauseGame();
    setShowPause(true);
    vibrateTap();
  };

  const handleResumeClick = () => {
    setShowPause(false);
    resumeGame();
  };

  const handleRestartClick = () => {
    setShowPause(false);
    setShowCountdown(true);
    setCurrentAchievement(null);
    displayedAchievementIds.current.clear();
    setPointsAnimations([]);
    hasNavigated.current = false;
    isGameEnding.current = false;
    isShowingAchievement.current = false;
  };

  const handleMenuClick = () => {
    navigate('/');
  };

  const handleToggleMute = () => {
    const newMuted = audioManager.toggleMute();
    setIsMuted(newMuted);
    vibrateTap();
  };

  const handleBubblePop = (id, bubbleData) => {
    if (gameState === 'ended' || isGameEnding.current) {
      return;
    }

    queueMicrotask(() => {
      audioManager.playPop();
      vibrateTap();
    });

    handlePop(id, bubbleData);

    const bubble = bubbles.find(b => b.id === id);
    if (bubble) {
      let points = bubbleData.points || 10;
      if (combo > 1) {
        points = Math.round(points * (1 + combo * 0.15));
      }
      if (powerups.combo.active) {
        points *= powerups.combo.multiplier;
      }

      const newAnimation = {
        id: Date.now() + Math.random(),
        x: bubble.x + bubble.size / 2,
        y: bubble.y,
        points,
        color: bubbleData.color
      };

      setPointsAnimations(prev => [...prev, newAnimation].slice(-8));
    }
  };

  const removePointsAnimation = (id) => {
    setPointsAnimations(prev => prev.filter(a => a.id !== id));
  };

  const handleAchievementComplete = () => {
    console.log('Achievement notification complete');
    setCurrentAchievement(null);
    isShowingAchievement.current = false;
  };

  const handleNotificationComplete = (notificationId) => {
    removeNotification(notificationId);
  };

  const comboMultiplier = Math.min(1 + combo * 0.15, 3);

  if (showCountdown) {
    return (
      <>
        <SEOHead 
          title={`${getModeTitle()} - 泡泡樂園`}
          description={`正在進入${getModeTitle()}，準備開始你的泡泡冒險！`}
        />
        <GameBackground />
        <Countdown onComplete={handleCountdownComplete} />
      </>
    );
  }

  return (
    <>
      <SEOHead 
        title={`${getModeTitle()} - 泡泡樂園`}
        description={`正在遊玩${getModeTitle()}，當前分數：${score}，等級：${level}`}
      />
      
      <GameBackground />
      
      <div className="fixed inset-0 overflow-hidden" role="main" aria-label="遊戲畫面">
        {bubbles.map(bubble => (
          <EnhancedBubble
            key={bubble.id}
            data={bubble}
            onPop={handleBubblePop}
            onRemove={handleRemove}
          />
        ))}

        {isMobile ? (
          <>
            <MobileHUD
              score={score}
              time={timeLeft}
              level={level}
              combo={combo}
            />
            <MobileControls
              onPause={handlePauseClick}
              isMuted={isMuted}
              onToggleMute={handleToggleMute}
            />
          </>
        ) : (
          <HUD
            score={score}
            time={timeLeft}
            level={level}
            combo={combo}
            onPause={handlePauseClick}
          />
        )}

        <SpeedIndicator level={level} />
        <ComboDisplay combo={combo} multiplier={comboMultiplier} />
        <Powerups combo={powerups.combo} time={powerups.time} />

        {isMobile && <TouchFeedback />}

        {pointsAnimations.map(anim => (
          <PointsAnimation
            key={anim.id}
            x={anim.x}
            y={anim.y}
            points={anim.points}
            color={anim.color}
            onComplete={() => removePointsAnimation(anim.id)}
          />
        ))}

        {notifications.map(notification => {
          if (notification.type === 'levelup') {
            return (
              <LevelUpNotification
                key={notification.id}
                level={notification.level}
                onComplete={() => handleNotificationComplete(notification.id)}
              />
            );
          }
          if (notification.type === 'powerup') {
            return (
              <PowerupNotification
                key={notification.id}
                type={notification.type}
                onComplete={() => handleNotificationComplete(notification.id)}
              />
            );
          }
          return null;
        })}

        {currentAchievement && gameState !== 'ended' && (
          <AchievementNotification
            achievement={currentAchievement}
            onComplete={handleAchievementComplete}
          />
        )}

        <PauseMenu
          isOpen={showPause}
          onResume={handleResumeClick}
          onRestart={handleRestartClick}
          onMenu={handleMenuClick}
          onHelp={() => {
            setShowPause(false);
            setShowHelp(true);
          }}
          onAchievements={() => {
            setShowPause(false);
            setShowAchievements(true);
          }}
        />

        <HelpPanel
          isOpen={showHelp}
          onClose={() => {
            setShowHelp(false);
            if (gameState === 'paused') setShowPause(true);
          }}
        />

        <AchievementsPanel
          isOpen={showAchievements}
          onClose={() => {
            setShowAchievements(false);
            if (gameState === 'paused') setShowPause(true);
          }}
          unlockedIds={stats.achievements}
        />

        <PerformanceMonitor enabled={showPerformance} />
      </div>
    </>
  );
};

export default GamePlay;